import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useLiveStreamAppContext } from "../ContextStore/Store";
import {
  buildReport,
  collectAcsEnvironment,
  collectSystemInfo,
  copyTextToClipboard,
  createAudioContext,
  generateReportId,
  getConsoleEntries,
  getDeviceHints,
  listDevices,
  playTestTone,
  queryPermission,
  testCamera,
  testMicrophone,
} from "../utils/MediaDiagnostics";

const STATUS_LABEL = {
  idle: "Not checked",
  running: "Checking…",
  pass: "Working",
  warn: "Needs attention",
  fail: "Problem found",
  waiting: "Waiting for you",
};

const STEP_TEXT = {
  system: "Checking your device and browser…",
  camera: "Testing your camera…",
  microphone: "Testing your microphone - please say something…",
  speaker: "Playing a test sound…",
};

// Browsers without HTMLMediaElement.setSinkId (Safari, iOS, Android) always play through the
// system output, so there is nothing to pick - same check as MediaDeviceSelector
const isSpeakerSelectionSupported =
  typeof HTMLMediaElement !== "undefined" && "setSinkId" in HTMLMediaElement.prototype;

const uniqueById = (devices = []) =>
  Array.from(new Set(devices.map((device) => device.deviceId))).map((id) =>
    devices.find((device) => device.deviceId === id)
  );

//Following Component is the Troubleshooting Popup on the Preview Screen - tests camera, microphone
//and speaker, lets the user switch device and retest, and builds a log for Vagaro Support
function TroubleshootingModal(props) {
  const {
    camera,
    microphone,
    speaker,
    setCamera,
    setMicrophone,
    setSpeaker,
    cameras,
    microphones,
    speakers,
    isCam,
    isMic,
    role,
    eventType,
    cameraPermission,
    microphonePermission,
    toggleStates,
    previewLocalVideoStream,
    __merchantId,
    __userId,
    __appConfirmUniqueID,
    appInsightTrack,
  } = useLiveStreamAppContext();

  const [phase, setPhase] = useState("idle");
  const [runningTest, setRunningTest] = useState(null);
  const [results, setResults] = useState({});
  const [micLevel, setMicLevel] = useState(0);
  const [speakerHeard, setSpeakerHeard] = useState(null);
  const [reportMeta, setReportMeta] = useState(null);
  const [copyState, setCopyState] = useState("");
  const [showLog, setShowLog] = useState(false);
  const audioContextRef = useRef(null);
  const resultsRef = useRef({});
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      audioContextRef.current?.close?.().catch(() => {});
    };
  }, []);

  const update = (patch) => {
    resultsRef.current = { ...resultsRef.current, ...patch };
    isMountedRef.current && setResults(resultsRef.current);
  };

  // Must run synchronously inside the click handler - Safari only lets audio started by a click play
  const getAudioContext = () => {
    if (!audioContextRef.current || audioContextRef.current.state === "closed") {
      audioContextRef.current = createAudioContext();
    }
    return audioContextRef.current;
  };

  const runCameraTest = async () => {
    setRunningTest("camera");
    update({ camera: await testCamera({ deviceId: camera, previewLocalVideoStream: isCam ? previewLocalVideoStream : null }) });
  };

  const runMicrophoneTest = async (audioContext) => {
    setRunningTest("microphone");
    update({
      microphone: await testMicrophone({
        deviceId: microphone,
        audioContext,
        onLevel: (level) => isMountedRef.current && setMicLevel(level),
      }),
    });
  };

  const runSpeakerTest = async (audioContext) => {
    setRunningTest("speaker");
    setSpeakerHeard(null);
    update({ speakerTone: await playTestTone({ deviceId: speaker, audioContext }) });
  };

  // Also send the summary to App Insights so Support can look it up by Report ID even if
  // the customer never pastes the log
  const sendSummary = (reportId, trigger) => {
    const collected = resultsRef.current;
    try {
      appInsightTrack(__merchantId, __userId, "LiveStream Troubleshooting Report", {
        reportId,
        trigger,
        appointmentId: __appConfirmUniqueID,
        camera: collected.camera?.status,
        cameraProblem: collected.camera?.problem,
        cameraAttempts: (collected.camera?.attempts || []).map(({ frames, ...attempt }) => {
          const { snapshot, ...frameStats } = frames || {};
          return { ...attempt, frames: frameStats };
        }),
        microphone: collected.microphone?.status,
        microphoneProblem: collected.microphone?.problem,
        speakerTone: collected.speakerTone,
        permissions: collected.permissions,
        acs: collected.acs,
        os: collected.system?.["Operating system"],
        browser: collected.system?.Browser,
        device: collected.system?.["Device model"],
        gpu: collected.system?.GPU,
        recentErrors: getConsoleEntries().filter((entry) => /\[(error|warn|uncaught|unhandled-rejection)\]/.test(entry)).slice(-20),
      }, reportId);
    } catch (e) {
    }
  };

  const finish = (reportId, trigger) => {
    if (!isMountedRef.current) return;
    setRunningTest(null);
    setPhase("done");
    setCopyState("");
    setReportMeta({ reportId, generatedAt: new Date() });
    sendSummary(reportId, trigger);
  };

  const runChecks = async () => {
    if (phase === "running") return;
    const audioContext = getAudioContext();
    setPhase("running");
    resultsRef.current = {};
    setResults({});
    setSpeakerHeard(null);
    setCopyState("");
    setShowLog(false);
    const reportId = generateReportId();

    try {
      setRunningTest("system");
      const [system, acs] = await Promise.all([collectSystemInfo(), collectAcsEnvironment()]);
      const permissions = {
        "Camera (browser)": await queryPermission("camera"),
        "Microphone (browser)": await queryPermission("microphone"),
        "Camera (app state)": cameraPermission,
        "Microphone (app state)": microphonePermission,
      };
      update({ system, acs, permissions, devicesBefore: await listDevices() });
      await runCameraTest();
      await runMicrophoneTest(audioContext);
      await runSpeakerTest(audioContext);
      update({ devicesAfter: await listDevices() });
    } catch (error) {
      update({ unexpectedError: `${error.name}: ${error.message}` });
    }
    finish(reportId, "full");
  };

  // Retest a single device (usually after switching it in the dropdown); keeps the same
  // Report ID so the log Support receives is still one report
  const retest = async (key) => {
    if (phase === "running") return;
    const audioContext = key === "camera" ? null : getAudioContext();
    setPhase("running");
    setShowLog(false);
    try {
      if (key === "camera") await runCameraTest();
      if (key === "microphone") await runMicrophoneTest(audioContext);
      if (key === "speaker") await runSpeakerTest(audioContext);
      update({ devicesAfter: await listDevices() });
    } catch (error) {
      update({ unexpectedError: `${error.name}: ${error.message}` });
    }
    finish(reportMeta?.reportId || generateReportId(), `retest-${key}`);
  };

  // Same persistence as MediaDeviceSelector so the choice survives the preview -> call navigation.
  // The old result no longer describes the selected device, so it is cleared until retested
  const handleDeviceChange = (key, deviceId) => {
    if (!deviceId) return;
    if (key === "camera") setCamera(deviceId);
    if (key === "microphone") setMicrophone(deviceId);
    if (key === "speaker") setSpeaker(deviceId);
    sessionStorage.setItem(key, deviceId);
    if (key === "speaker") {
      setSpeakerHeard(null);
      update({ speakerTone: undefined });
    } else {
      update({ [key]: undefined });
    }
    setCopyState("");
  };

  const speakerResult = useMemo(() => {
    const tone = results.speakerTone;
    if (!tone) return null;
    if (!tone.played) return { status: "fail", problem: `The test sound could not be played. ${tone.error || ""}`.trim(), ...tone };
    if (speakerHeard === true) return { status: "pass", heardByUser: "Yes", ...tone };
    if (speakerHeard === false) {
      return {
        status: "fail",
        heardByUser: "No",
        problem: isSpeakerSelectionSupported
          ? "The test sound was not heard. Pick a different speaker above and press Retest, and check the volume and mute keys."
          : "The test sound was not heard. Check the volume and mute keys, and the output device in your system sound settings.",
        ...tone,
      };
    }
    return { status: "waiting", heardByUser: "Not answered", ...tone };
  }, [results.speakerTone, speakerHeard]);

  const findings = useMemo(() => {
    const list = [];
    const { system = {}, acs = {}, permissions = {}, devicesBefore = [], devicesAfter = [] } = results;
    const devices = devicesAfter.length ? devicesAfter : devicesBefore;
    if (permissions["Camera (browser)"] === "denied") list.push("Camera permission is blocked for this site in the browser.");
    if (permissions["Microphone (browser)"] === "denied") list.push("Microphone permission is blocked for this site in the browser.");
    if (system["Secure context (HTTPS)"] === "NO") list.push("The page is not running on HTTPS, so the browser will not allow camera/microphone.");
    if (system["Page policy - camera"] === "BLOCKED") list.push("The page that embeds LiveStream does not allow camera access (iframe policy).");
    if (system["Page policy - microphone"] === "BLOCKED") list.push("The page that embeds LiveStream does not allow microphone access (iframe policy).");
    if (acs["Supported environment"] === "NO") list.push(`This browser/OS combination is not supported by the video service (${acs["Detected as"]}). Please update the browser or use the latest Google Chrome.`);
    if (acs["Video call open in another tab"] === "YES") list.push("A LiveStream session is open in another browser tab. Close other tabs - they can hold the camera.");
    if (results.devicesBefore && !devices.some((d) => d.kind === "videoinput")) list.push("No camera was detected on this device.");
    if (results.devicesBefore && !devices.some((d) => d.kind === "audioinput")) list.push("No microphone was detected on this device.");
    if (!isCam) list.push("The camera is turned off in the preview (camera button).");
    if (!isMic) list.push("The microphone is muted in the preview (microphone button).");
    if (/^Safari/.test(system.Browser || "") && (results.camera?.status === "fail" || results.camera?.status === "warn")) {
      list.push("If the camera keeps failing in Safari, join with the latest Google Chrome while we investigate.");
    }
    if (results.unexpectedError) list.push(`The troubleshooter itself hit an error: ${results.unexpectedError}`);
    return [...list, ...getDeviceHints(devices)];
  }, [results, isCam, isMic]);

  const report = useMemo(() => {
    if (!reportMeta) return "";
    return buildReport({
      ...reportMeta,
      appState: {
        Role: role,
        "Event type": eventType === 1 ? "Appointment" : "Class",
        "Business ID": __merchantId,
        "User ID": __userId,
        "Appointment ID": __appConfirmUniqueID,
        Page: window.location.pathname,
        "Camera on in preview": isCam ? "Yes" : "No",
        "Microphone on in preview": isMic ? "Yes" : "No",
        "Blur background": toggleStates?.blurBackground ? "On" : "Off",
        "Remove background noise": toggleStates?.removeBackgroundNoise ? "On" : "Off",
        "Mirror my video": toggleStates?.mirrorMyVideo ? "On" : "Off",
        "Page visible": document.visibilityState,
        selectedCameraId: camera,
        selectedMicrophoneId: microphone,
        selectedSpeakerId: speaker,
      },
      system: results.system,
      acs: results.acs,
      permissions: results.permissions,
      devicesBefore: results.devicesBefore,
      devicesAfter: results.devicesAfter,
      camera: results.camera,
      microphone: results.microphone,
      speaker: speakerResult,
      findings,
      consoleLogs: getConsoleEntries(),
    });
  }, [reportMeta, results, speakerResult, findings, role, eventType, __merchantId, __userId, __appConfirmUniqueID, isCam, isMic, toggleStates, camera, microphone, speaker]);

  const handleCopy = async () => {
    const copied = await copyTextToClipboard(report);
    setCopyState(copied ? "copied" : "failed");
    if (!copied) setShowLog(true);
  };

  const statusFor = (key, result) => {
    if (runningTest === key) return "running";
    return result ? result.status : "idle";
  };

  const rows = [
    { key: "camera", title: "Camera", icon: "va-video", result: results.camera, devices: uniqueById(cameras), selected: camera, permission: cameraPermission },
    { key: "microphone", title: "Microphone", icon: "va-microphone", result: results.microphone, devices: uniqueById(microphones), selected: microphone, permission: microphonePermission },
    { key: "speaker", title: "Speaker", icon: "fa-volume-high", result: speakerResult, devices: isSpeakerSelectionSupported ? uniqueById(speakers) : [], selected: speaker },
  ];
  const isBusy = phase === "running";

  return (
    <Modal
      className="livestreamPopup troubleshoot-popup"
      show={props.show}
      onHide={() => props.onHide(false)}
      size="md"
      aria-labelledby="troubleshoot-modal-title"
      centered
    >
      <div className="">
        <Modal.Header>
          <Modal.Title id="troubleshoot-modal-title">
            <h4 className="modal-title">Troubleshoot Camera & Audio</h4>
            <i
              className="font-awesome-light-pro live-popup-close"
              onClick={() => props.onHide(false)}
              style={{ cursor: "pointer" }}
            >
              &#xf00d;
            </i>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="clearfix default-scrollbar">
          <div className="discText">
            <p className="troubleshoot-intro">
              {phase === "done"
                ? "If something isn't working, pick a different device and press Retest. Then copy the log and send it to Vagaro Support."
                : "We'll test your camera, microphone and speaker. Your camera picture is only shown to you and is not included in the log."}
            </p>
          </div>

          <ul className="troubleshoot-list">
            {rows.map(({ key, title, icon, result, devices, selected, permission }) => {
              const status = statusFor(key, result);
              const canSelect = devices.length > 0 && permission !== "denied";
              return (
                <li key={key} className={`troubleshoot-item ts-${status}`}>
                  <div className="troubleshoot-item-head">
                    <i className={`icon fas font-awesome-solid-pro ${icon}`} aria-hidden="true"></i>
                    <span className="troubleshoot-item-title">{title}</span>
                    <span className="troubleshoot-status">{STATUS_LABEL[status]}</span>
                  </div>

                  <div className="troubleshoot-device">
                    {canSelect ? (
                      <select
                        className="troubleshoot-select"
                        aria-label={`Select ${title}`}
                        value={devices.some((d) => d.deviceId === selected) ? selected : ""}
                        disabled={isBusy}
                        onChange={(e) => handleDeviceChange(key, e.target.value)}
                      >
                        {!devices.some((d) => d.deviceId === selected) && <option value="">Select a {title}</option>}
                        {devices.map((device) => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {device.label || `Unnamed ${title}`}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="troubleshoot-device-note">
                        {key === "speaker" && !isSpeakerSelectionSupported
                          ? "Uses your system sound output"
                          : permission === "denied"
                            ? `${title} access is blocked`
                            : `No ${title.toLowerCase()} found`}
                      </span>
                    )}
                    {phase === "done" && (
                      <button className="cbtn brd-btn troubleshoot-retest" disabled={isBusy} onClick={() => retest(key)}>
                        Retest
                      </button>
                    )}
                  </div>

                  {key === "camera" && result?.snapshot && (
                    <img className="troubleshoot-snapshot" src={result.snapshot} alt="Camera test snapshot" />
                  )}
                  {key === "microphone" && status === "running" && (
                    <div className="troubleshoot-meter" aria-label="Microphone level">
                      <span style={{ width: `${Math.round(micLevel * 100)}%` }}></span>
                    </div>
                  )}
                  {key === "speaker" && status === "waiting" && (
                    <div className="troubleshoot-question">
                      <span>Did you hear the test sound?</span>
                      <button className="cbtn brd-btn" onClick={() => setSpeakerHeard(true)}>Yes</button>
                      <button className="cbtn brd-btn" onClick={() => setSpeakerHeard(false)}>No</button>
                    </div>
                  )}
                  {result?.problem && <p className="troubleshoot-problem">{result.problem}</p>}
                  {result?.note && <p className="troubleshoot-note">{result.note}</p>}
                </li>
              );
            })}
          </ul>

          {runningTest && <p className="troubleshoot-step">{STEP_TEXT[runningTest]}</p>}

          {phase === "done" && findings.length > 0 && (
            <div className="troubleshoot-findings">
              <span className="troubleshoot-findings-title">What we found</span>
              <ul>
                {findings.map((finding) => (
                  <li key={finding}>{finding}</li>
                ))}
              </ul>
            </div>
          )}

          {phase === "done" && (
            <div className="troubleshoot-report">
              <div className="troubleshoot-report-id">
                Report ID: <strong>{reportMeta?.reportId}</strong>
                <button className="troubleshoot-link" onClick={() => setShowLog((prev) => !prev)}>
                  {showLog ? "Hide log" : "Show log"}
                </button>
              </div>
              {copyState === "copied" && <p className="troubleshoot-note">Log copied - paste it into your reply to Vagaro Support.</p>}
              {copyState === "failed" && <p className="troubleshoot-problem">Couldn't copy automatically. Select the log below and copy it manually.</p>}
              {showLog && <textarea className="troubleshoot-log" readOnly value={report} onFocus={(e) => e.target.select()} />}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="cbtn brd-btn margin-right16" onClick={() => props.onHide(false)}>
            Close
          </button>
          {phase === "done" && (
            <button className="cbtn brd-btn margin-right16" onClick={runChecks}>
              Run All Again
            </button>
          )}
          {phase === "done" ? (
            <button className="cbtn thm-btn" onClick={handleCopy}>
              {copyState === "copied" ? "Copied" : "Copy Log"}
            </button>
          ) : (
            <button className="cbtn thm-btn" disabled={isBusy} onClick={runChecks}>
              {isBusy ? "Checking…" : "Start Checks"}
            </button>
          )}
        </Modal.Footer>
      </div>
    </Modal>
  );
}

export default memo(TroubleshootingModal);
